import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Instagram, Facebook } from "lucide-react";

const INSTAGRAM_URL = "https://www.instagram.com/takomstmenaam/";
const FACEBOOK_URL = "https://www.facebook.com/profile.php?id=61591858686559";

const links = [
  { label: "Situatie", href: "#situatie" },
  { label: "Planning", href: "#planning" },
  { label: "Ideeën", href: "#ideeen" },
  { label: "Meedoen", href: "#meedoen" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[#FAF7F2]/85 backdrop-blur-xl border-b border-[#1A2E22]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center transition-opacity hover:opacity-80"
          aria-label="Takomst fan Menaam"
        >
          <img
            src="/logo-header.png"
            alt="Takomst fan Menaam — mei elkoar, foar elkoar"
            className={`h-10 sm:h-11 w-auto object-contain transition-[filter] duration-300 ${
              scrolled ? "" : "brightness-0 invert"
            }`}
          />
        </a>

        <ul className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors hover:text-[#74C69D] ${
                  scrolled ? "text-[#1A2E22]/75" : "text-[#FAF7F2]/85"
                }`}
              >
                {l.label}
              </a>
            </li>
          ))}
          <li>
            <Link
              to="/updates"
              data-testid="nav-link-updates"
              className={`text-sm font-medium transition-colors hover:text-[#74C69D] ${
                scrolled ? "text-[#1A2E22]/75" : "text-[#FAF7F2]/85"
              }`}
            >
              Updates
            </Link>
          </li>
          <li>
            <Link
              to="/historie"
              data-testid="nav-link-historie"
              className={`text-sm font-medium transition-colors hover:text-[#74C69D] ${
                scrolled ? "text-[#1A2E22]/75" : "text-[#FAF7F2]/85"
              }`}
            >
              Historie
            </Link>
          </li>
          <li>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-instagram"
              aria-label="Volg Takomst fan Menaam op Instagram"
              className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${
                scrolled
                  ? "border-[#1A2E22]/15 text-[#1A2E22]/75 hover:text-[#2D6A4F] hover:border-[#2D6A4F]/40"
                  : "border-white/25 text-[#FAF7F2]/85 hover:text-[#74C69D] hover:border-[#74C69D]/60"
              }`}
            >
              <Instagram size={16} />
            </a>
          </li>
          <li>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-facebook"
              aria-label="Volg Takomst fan Menaam op Facebook"
              className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${
                scrolled
                  ? "border-[#1A2E22]/15 text-[#1A2E22]/75 hover:text-[#2D6A4F] hover:border-[#2D6A4F]/40"
                  : "border-white/25 text-[#FAF7F2]/85 hover:text-[#74C69D] hover:border-[#74C69D]/60"
              }`}
            >
              <Facebook size={16} />
            </a>
          </li>
          <li>
            <a
              href="#meedoen"
              data-testid="nav-cta"
              className="text-sm font-bold px-5 py-2.5 rounded-full bg-[#74C69D] text-[#1A2E22] hover:bg-[#8fd4b0] transition-colors"
            >
              Doe mee
            </a>
          </li>
        </ul>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`md:hidden p-2 ${scrolled ? "text-[#1A2E22]" : "text-[#FAF7F2]"}`}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#1A2E22] px-5 py-4" data-testid="nav-mobile-menu">
          <ul className="flex flex-col gap-1">
            {links.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="block py-3 text-[#FAF7F2]/85 font-medium border-b border-white/5"
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li>
              <Link
                to="/updates"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-link-updates"
                className="block py-3 text-[#FAF7F2]/85 font-medium border-b border-white/5"
              >
                Updates
              </Link>
            </li>
            <li>
              <Link
                to="/historie"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-link-historie"
                className="block py-3 text-[#FAF7F2]/85 font-medium border-b border-white/5"
              >
                Historie
              </Link>
            </li>
            <li>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-instagram"
                className="flex items-center gap-2.5 py-3 text-[#FAF7F2]/85 font-medium border-b border-white/5"
              >
                <Instagram size={16} /> Instagram
              </a>
            </li>
            <li>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-facebook"
                className="flex items-center gap-2.5 py-3 text-[#FAF7F2]/85 font-medium border-b border-white/5"
              >
                <Facebook size={16} /> Facebook
              </a>
            </li>
            <li>
              <a
                href="#meedoen"
                onClick={() => setOpen(false)}
                className="block mt-3 text-center font-bold px-5 py-3 rounded-full bg-[#74C69D] text-[#1A2E22]"
              >
                Doe mee
              </a>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
}
